import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  acceptBuildingInvite, fetchBuildingInvite, getDisplayErrorMessage, requestBuildingInviteSignInLink,
  type BuildingInviteState, type InviteSignIn,
} from '@/lib/api';
import { AuthLayout } from '@/components/AuthLayout';
import { InviteSignInPanel } from '@/components/ui/InviteSignInPanel';
import {
  INVITE_SIGN_IN_COPY, buildingInviteRoleLabel, otherAccountLine, readInvitePageUrl, takeInviteReturn,
} from '@/lib/inviteSignIn';
import formStyles from '@/components/AuthForm.module.css';
import styles from './MagicLinkVerifyPage.module.css';

interface Props {
  onNavigate: (path: string) => void;
}

/**
 * The building staff invite link (Warren, 28 Oct 2026, item 5):
 * /building-invite?token=... from the invite email. Works signed in or out
 * (hoisted above the auth guard like the magic-link verify page): it says
 * who invited them to which location. A new person's Accept sets up their
 * login and then signs them in through the ordinary magic-link verify (MFA
 * set-up next, as for every Admin login).
 *
 * An email that already has an account (29 October 2026, Warren: "accepting
 * it asks them to sign in and adds the new role to that account, never
 * 'email taken'"): "You already have an EV Initiative account with this
 * email. Sign in to add the chargers at <location> to it." The person signs
 * in as that account here (password, a sign-in link to its mailbox, Google,
 * or the Admin session already open as it), and only then is the location
 * added; then their authenticator step. Signed in as someone else: refused.
 * EV Initiative staff sign in as themselves too, and then sign in as usual.
 */
export function BuildingInvitePage({ onNavigate }: Props) {
  // The invite token (or the one kept for Google's return) and a one-time sign-in token from the emailed sign-in
  // link or Google (?sign_in=), stripped from the address bar at once.
  const arrival = useMemo(() => {
    const url = readInvitePageUrl(window.location.search);
    if (url.signIn) {
      try { window.history.replaceState(null, '', `${window.location.pathname}${url.rest ? `?${url.rest}` : ''}`); } catch { /* keep going */ }
    }
    return { token: url.token || takeInviteReturn('building_staff') || '', signIn: url.signIn };
  }, []);
  const token = arrival.token;
  const [state, setState] = useState<BuildingInviteState | null>(null);
  const [error, setError] = useState('');
  const [acceptError, setAcceptError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState('');
  const [signInUsed, setSignInUsed] = useState(false);

  useEffect(() => {
    if (!token) { setError('This invite link is incomplete. Open it from the email again.'); return; }
    fetchBuildingInvite(token)
      .then(setState)
      .catch(async (err: unknown) => setError(await getDisplayErrorMessage(err, 'This invite link is not valid. Ask whoever invited you to send it again.')));
  }, [token]);

  const accept = async (signIn?: InviteSignIn): Promise<void> => {
    setAccepting(true);
    setAcceptError('');
    try {
      const out = await acceptBuildingInvite(token, signIn);
      if (out.sign_in_path) {
        window.location.assign(out.sign_in_path);
        return;
      }
      setDone(out.outcome === 'staff'
        ? `You already see every site, ${out.location_name} included. Sign in as usual.`
        : `${out.location_name} is added to your account.`);
    } catch (err) {
      setAcceptError(await getDisplayErrorMessage(err, 'The invite could not be accepted. Try again.'));
    } finally {
      setAccepting(false);
    }
  };

  // Back from the emailed sign-in link or Google: accept with it once, as soon as the invite is read.
  useEffect(() => {
    if (signInUsed || !arrival.signIn || !state || state.status !== 'pending' || state.account === 'new') return;
    setSignInUsed(true);
    void accept({ link: arrival.signIn });
  }, [state, signInUsed]);

  const title = state ? `Join ${state.location_name}` : 'Your invite';
  const subtitle = state?.inviter_name ? `${state.inviter_name} invited you` : 'EV Initiative';
  const existing = state ? state.account !== 'new' : false;

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      {!state && !error && (
        <div className={styles.spinnerWrap}><Loader2 size={36} className={styles.spinner} /></div>
      )}
      {error && (
        <div className={styles.errorStack}>
          <div className={formStyles.serverError} role="alert">
            <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {error}
          </div>
          <button type="button" className={formStyles.btnSecondary} onClick={() => onNavigate('/login')}>Go to sign in</button>
        </div>
      )}
      {state && !error && (
        <div className={styles.errorStack}>
          {state.status === 'pending' && !done && (
            <>
              <p>
                {state.inviter_name ?? 'EV Initiative'} invited {state.name || state.email} to the EV chargers at{' '}
                <strong>{state.location_name}</strong>
                {state.role_label !== 'Other' ? ` as ${state.role_label}` : ''}.
              </p>
              {state.property_manager_access ? <p>You will have full Property Manager access to this location, including all its chargers, sessions, reports and bank setup. No other locations are included.</p> : <p>
                You will see the chargers there, whether each one is online, and their alerts, with the steps for a
                tripped breaker or a restart. EV Initiative also emails you when a charger goes offline or keeps
                refusing to start.
              </p>}
              {state.signed_in === 'other' ? (
                <div className={formStyles.serverError} role="alert">{otherAccountLine(state.email)}</div>
              ) : existing && state.signed_in === 'invitee' ? (
                <>
                  <p>{INVITE_SIGN_IN_COPY.signedInAs.replace('{email}', state.email)}</p>
                  {acceptError && <div className={formStyles.serverError} role="alert">{acceptError}</div>}
                  <button type="button" className={formStyles.submitBtn} disabled={accepting} onClick={() => { void accept(); }}>
                    {accepting ? 'Accepting...' : INVITE_SIGN_IN_COPY.acceptSignedIn}
                  </button>
                </>
              ) : existing ? (
                <InviteSignInPanel
                  kind="building_staff"
                  token={token}
                  email={state.email}
                  roleLabel={buildingInviteRoleLabel(state.location_name)}
                  busy={accepting}
                  error={acceptError}
                  onAccept={accept}
                  onRequestLink={() => requestBuildingInviteSignInLink(token)}
                />
              ) : (
                <>
                  <p>Accepting makes a login for {state.email}. You will set up a sign-in code app next, as every Admin login does.</p>
                  {acceptError && <div className={formStyles.serverError} role="alert">{acceptError}</div>}
                  <button type="button" className={formStyles.submitBtn} disabled={accepting} onClick={() => { void accept(); }}>
                    {accepting ? 'Accepting...' : 'Accept the invite'}
                  </button>
                </>
              )}
            </>
          )}
          {done && (
            <>
              <p role="status">{done}</p>
              <button type="button" className={formStyles.submitBtn} onClick={() => onNavigate('/login')}>Sign in</button>
            </>
          )}
          {state.status === 'accepted' && (
            <>
              <p role="status">This invite was already accepted. Sign in with {state.email}.</p>
              <button type="button" className={formStyles.submitBtn} onClick={() => onNavigate('/login')}>Sign in</button>
            </>
          )}
          {(state.status === 'expired' || state.status === 'revoked') && (
            <div className={formStyles.serverError} role="alert">
              {state.status === 'expired'
                ? `This invite has expired. Ask ${state.inviter_name ?? 'whoever invited you'} to send it again.`
                : 'This invite was withdrawn. Ask whoever invited you to send a new one.'}
            </div>
          )}
        </div>
      )}
    </AuthLayout>
  );
}

export default BuildingInvitePage;
